import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

type BillingTransactionContextValue = {
  isTransactionPending: boolean
  runTransaction: <T>(fn: () => Promise<T>) => Promise<T>
  acquireTransactionLock: () => () => void
}

const BillingTransactionContext = createContext<
  BillingTransactionContextValue | undefined
>(undefined)

export function BillingTransactionProvider(props: {children: React.ReactNode}) {
  const [pendingCount, setPendingCount] = useState(0)

  const acquireTransactionLock = useCallback(() => {
    setPendingCount((count) => count + 1)

    let released = false

    return () => {
      if (released) {
        return
      }

      released = true
      setPendingCount((count) => Math.max(0, count - 1))
    }
  }, [])

  const runTransaction = useCallback(
    async <T,>(fn: () => Promise<T>) => {
      const release = acquireTransactionLock()

      try {
        return await fn()
      } finally {
        release()
      }
    },
    [acquireTransactionLock],
  )

  const value = useMemo(
    () => ({
      isTransactionPending: pendingCount > 0,
      runTransaction,
      acquireTransactionLock,
    }),
    [pendingCount, runTransaction, acquireTransactionLock],
  )

  return (
    <BillingTransactionContext.Provider value={value}>
      {props.children}
    </BillingTransactionContext.Provider>
  )
}

export function useBillingTransaction() {
  const context = useContext(BillingTransactionContext)

  if (context === undefined) {
    throw new Error(
      'useBillingTransaction must be used within a BillingTransactionProvider',
    )
  }

  return context
}

export function useOptionalBillingTransaction() {
  return useContext(BillingTransactionContext)
}

const passthroughRunTransaction = <T,>(fn: () => Promise<T>) => fn()

const noopReleaseTransactionLock = () => () => {}

const defaultBillingTransactionContext: BillingTransactionContextValue = {
  isTransactionPending: false,
  runTransaction: passthroughRunTransaction,
  acquireTransactionLock: () => noopReleaseTransactionLock,
}

/** Use where billing UI may render outside BillingRoutes (e.g. org add-ons). */
export function useBillingTransactionOrPassthrough() {
  return useOptionalBillingTransaction() ?? defaultBillingTransactionContext
}
